import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { Button, Checkbox, Heading, Message, Text, useIsBreakpointLargeAndUp, useModal } from '@afterpaytouch/core'
import {
  ApplePayStatus,
  ApplePayError,
  CheckoutStatus,
  isCardScanRequiredErrorCode,
  NotificationError,
  PageLayout,
  PaymentMethodType,
  RecoverableErrorCode,
  PaymentSourceCaptureCardTypes,
  AutopayConfirmationModalKey,
  PayMonthlyTILAModalKey,
  Features,
} from '../../model'
import { ConsumerLendingPlanDetails, DisabledModal, OrderSummary, PaymentMethod, ShippingSummary, StartedStatusGuard } from '../../component'
import { Country, ErrorCode } from '@afterpaytouch/portal-api'
import { Route } from '../../config/router'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import {
  selectPaymentMethod,
  setCardsForExpiryLimit,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCountry,
  useHasUserInitiatedChangedPaymentMethod,
  useIsDeferredShipping,
  useIsExpress,
  useIsPickup,
  useIsReadyToConfirm,
  usePreferredBankAccount,
  usePreferredCard,
  usePreferredPaymentMethod,
  useSelectedPaymentMethod,
  useApplePayState,
  useApplePayFeature,
  resetCheckoutError,
  useCachedFeatureFlag,
} from '../../state'
import styles from './style.module.css'
import { AutopayButton, AutopayModal } from '../../component/AutopayToggle'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { NonTerminalErrors, TrackingEvent } from '../../model/monitoring'
import { PlanOverview } from './PlanOverview'
import { useGetIsAutoPayAgreed } from '../../component/AutopayToggle/hooks'
import { Terms as TILATerms } from '../../component/TruthInLending/Terms'
import { TruthInLendingModal } from '../../component/TruthInLending/TruthInLendingModal'
import { TruthInLendingButton } from '../../component/TruthInLending/TruthInLendingButton'
import { Head } from '../../component'

export interface PayMonthlySummaryPageProps {
  /**
   * Tags the `VIEWED_SUMMARY_PAGE` event so the redesign A/B can distinguish the legacy page
   * from the redesigned shell (CASHDAP-3090). Defaults to the legacy marker.
   * Full PI4-shape page-view parity (firstPaymentType, etc.) lands in CASHDAP-3084.
   */
  summaryPageVersion?: string
}

export const PAY_MONTHLY_SUMMARY_VERSION_LEGACY = 'pm legacy'

export const PayMonthlySummaryPage: FunctionComponent<PayMonthlySummaryPageProps> = ({ summaryPageVersion = PAY_MONTHLY_SUMMARY_VERSION_LEGACY }) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const { status, error } = useCheckoutState()
  const applePay = useApplePayState()
  const { sectionHeadingSize, headingSize, textSize, smallTextSize } = useCheckoutSizes()
  const ready = useIsReadyToConfirm()
  const country = useCountry()
  const [cardExpiryMonthsEventSent, setCardExpiryMonthsEventSent] = useState<boolean>(false)
  const [notAcceptedPaymentMethodEventSent, setnotAcceptedPaymentMethodEventSent] = useState<boolean>(false)
  const [clicked, setClicked] = useState<boolean>(false)
  const [notification, setNotification] = useState<NotificationError>()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const { account, valid: bankAccountValid, reason: bankAccountReason } = usePreferredBankAccount() ?? {}
  const { card, valid, reason, switched } = usePreferredCard() ?? {}
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const userInitiatedChangedPaymentMethod = useHasUserInitiatedChangedPaymentMethod()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isDesktop = useIsBreakpointLargeAndUp()
  const isPickup = useIsPickup()
  const isExpress = useIsExpress()
  const isDeferredShipping = useIsDeferredShipping()
  const cardExpiryLimitInMonths = 2
  const applePayFeature = useApplePayFeature()
  const cashPayMonthlyEnabled = useCachedFeatureFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED, false)
  const { showModal: showAutopayConfirmationModal } = useModal(AutopayConfirmationModalKey)
  const autoPayAgreed = useGetIsAutoPayAgreed() ?? false
  const [documentId, setDocumentId] = useState<string>('')
  const [tilDisclosure, setTilDisclosure] = useState<boolean>(false)
  const [isTILATermsCheckboxChecked, setTILATermsCheckboxChecked] = useState<boolean>(false)
  const [showTILACheckboxErrorMessage, setShowTILACheckboxErrorMessage] = useState<boolean>(false)
  const { showModal: showPayMonthlyTILAModal } = useModal(PayMonthlyTILAModalKey)

  const setDocumentIdToShow = (documentId: string, isTilDisclosure: boolean = false): void => {
    showPayMonthlyTILAModal()
    setTilDisclosure(isTilDisclosure)
    setDocumentId(documentId)
  }

  const handleTILATermsCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const isChecked = e.target.checked
    setTILATermsCheckboxChecked(isChecked)
    if (isChecked) {
      setShowTILACheckboxErrorMessage(false)
    }
  }

  const renderTILACheckboxErrorMessage = (): ReactNode | null => {
    if (!showTILACheckboxErrorMessage) {
      return null
    }

    return (
      <div className={styles.tilaCheckboxErrorMessageContainer}>
        <Text color='Fire' size={smallTextSize}>
          {t('consumerLending:til.acceptToContinue')}
        </Text>
      </div>
    )
  }

  const handleButtonClick = async (): Promise<void> => {
    if (isTILATermsCheckboxChecked && autoPayAgreed) {
      showAutopayConfirmationModal()
    } else {
      setShowTILACheckboxErrorMessage(true)
    }
  }

  useEffect(() => {
    if (error?.extra !== undefined) {
      const { extra } = error
      if (extra.isEFAFinished) {
        dispatch(resetCheckoutError())
      }
    }
  }, [error])

  // this piece of code will also re-calculate cards' error reasons with cardExpiryLimitInMonths when switching plans from PayIn4
  useEffect(() => {
    if (error == null && !userInitiatedChangedPaymentMethod) {
      dispatch(setCardsForExpiryLimit(cardExpiryLimitInMonths))
    }
  }, [cardExpiryLimitInMonths, error])

  // this piece of code is to fix auto switch defect, which is caused by setCardsForExpiryLimit:
  // the selected payment method is not updated after setCardsForExpiryLimit
  useEffect(() => {
    if (!userInitiatedChangedPaymentMethod && card !== null && card?.error === null && selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD) {
      dispatch(
        selectPaymentMethod({
          card: {
            token: card.token,
          },
          type: PaymentMethodType.CREDIT_CARD,
        })
      )
    }
  }, [card])

  useEffect(() => {
    if (applePay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applePay.status])

  useEffect(() => {
    if (status <= CheckoutStatus.Loaded) {
      router.push(Route.LOGGED_IN)
    }

    if (status === CheckoutStatus.IdentityIneligible) {
      if (country === Country.GB) {
        router.push(Route.VERIFY_RETRY)
      } else {
        router.push(Route.VERIFY_IDENTITY)
      }
    }

    if (status === CheckoutStatus.LackOfEligiblePaymentMethod) {
      router.push(Route.ADD_PAYMENT_METHOD)
    }

    router.prefetch(Route.PAYMENT_METHOD)
  }, [country, router, status])

  useEffect(() => {
    if (error?.error !== 'undefined' && isCardScanRequiredErrorCode(error?.error as ErrorCode)) {
      return setClicked(false)
    } else if (typeof error?.error !== 'undefined' && Object.values(RecoverableErrorCode).includes(error?.error as RecoverableErrorCode)) {
      setClicked(false)
      setNotification({
        heading: t(`summary:error.${error?.error}.heading`),
        message: t(`summary:error.${error?.error}.message`),
        severe: true,
      })
    } else if (applePay.error !== undefined) {
      setClicked(false)
      if (applePay?.error === ApplePayError.InvalidCardType) {
        if (applePay?.cardType === PaymentSourceCaptureCardTypes.CREDIT) {
          setNotification({
            heading: t('summary:error.creditCardProhibitedForPaymentType.heading'),
            message: t('summary:error.creditCardProhibitedForPaymentType.message'),
            severe: false,
          })
        } else {
          setNotification({
            heading: t('summary:error.prepaidCard.heading'),
            message: t('summary:error.prepaidCard.message'),
            severe: false,
          })
        }
      } else if (applePay?.error === ApplePayError.BinBlocked) {
        setNotification({
          heading: t('summary:error.unsupportedCardIssuerBank.heading'),
          message: t('summary:error.unsupportedCardIssuerBank.message'),
          severe: false,
        })
      } else {
        setNotification({
          heading: t('summary:error.applePay.heading'),
          message: t('summary:error.applePay.message'),
          severe: true,
        })
      }
    }
  }, [error, applePay.error])

  // debit card should have higher priority than other payment methods.
  // other payment methods blocked errors will be displayed when no other debit cards on file.
  useEffect(() => {
    const switchToValidCard = selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD && card !== null && card?.error === null
    if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && switchToValidCard) {
      // need to show message that we've switched their payment method from ach to card.
      // card can be null due to the invocation of resetPreferredPaymentMethodErrors, so we don't want to show this error in that case.
      setNotification({
        heading: t(`consumerLending:bankAccountErrors.switchedToCard.heading`),
        message: t(`consumerLending:bankAccountErrors.switchedToCard.message`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT &&
      account !== null &&
      bankAccountReason !== null &&
      selectedPaymentMethod?.type === PaymentMethodType.ACH_BANK_ACCOUNT
    ) {
      if (!notAcceptedPaymentMethodEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: bankAccountReason })
        setnotAcceptedPaymentMethodEventSent(true)
      }

      setNotification({
        heading: t(`summary:bankAccountErrors.${bankAccountReason}.heading`),
        message: t(`summary:bankAccountErrors.${bankAccountReason}.message`),
        severe: !bankAccountValid,
      })
    } else if (preferredPaymentMethod === PaymentMethodType.APPLE_PAY && switchToValidCard && !applePayFeature.enabledForConsumerLending) {
      setNotification({
        heading: t(`consumerLending:applePayNotAllowed.heading_switched`),
        message: t(`consumerLending:applePayNotAllowed.message_switched`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.APPLE_PAY &&
      selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY &&
      !applePayFeature.enabledForConsumerLending
    ) {
      setNotification({
        heading: t(`consumerLending:applePayNotAllowed.heading_cannotSwitch`),
        message: t(`consumerLending:applePayNotAllowed.message_cannotSwitch`),
        severe: true,
      })
    } else if (preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY && switchToValidCard && !cashPayMonthlyEnabled) {
      setNotification({
        heading: t(`consumerLending:cashAppPayNotAllowed.heading_switched`),
        message: t(`consumerLending:cashAppPayNotAllowed.message_switched`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY &&
      selectedPaymentMethod?.type === PaymentMethodType.CASH_APP_PAY &&
      !cashPayMonthlyEnabled
    ) {
      setNotification({
        heading: t(`consumerLending:cashAppPayNotAllowed.heading_cannotSwitch`),
        message: t(`consumerLending:cashAppPayNotAllowed.message_cannotSwitch`),
        severe: true,
      })
    } else if (cardExpiryLimitInMonths !== null && selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD && reason === 'cardExpiryLimitInMonths') {
      const options = { context: switched ? 'switched' : 'cannotSwitch', months: cardExpiryLimitInMonths }

      if (!cardExpiryMonthsEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: NonTerminalErrors.ClCardExpiryError })
        setCardExpiryMonthsEventSent(true)
      }

      setNotification({
        heading: t(`summary:error.cardExpiryLimitInMonths.heading`, options),
        message: t(`summary:error.cardExpiryLimitInMonths.message`, options),
        severe: !valid,
      })
    } else if (card !== null && reason !== null) {
      if (!notAcceptedPaymentMethodEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: reason })
        setnotAcceptedPaymentMethodEventSent(true)
      }

      const options = { context: switched ? 'switched' : 'cannotSwitch' }
      setNotification({
        heading: t(`summary:error.${reason}.heading`, options),
        message: t(`summary:error.${reason}.message`, options),
        severe: !valid,
      })
    }
  }, [card, valid, reason, switched, account, preferredPaymentMethod, bankAccountReason, selectedPaymentMethod, cardExpiryLimitInMonths, cashPayMonthlyEnabled])

  if (status === CheckoutStatus.Error) {
    return null
  }

  const renderAutopayToggle = (): ReactNode | null => {
    if (country !== Country.US) {
      return null
    }

    return <AutopayButton />
  }

  const renderCTAButton = (): ReactNode | null => {
    if (!autoPayAgreed && isTILATermsCheckboxChecked) {
      return <TruthInLendingButton />
    }

    return (
      <Button
        padding='Fluid'
        loading={clicked}
        onClick={handleButtonClick}
        testNameSpace='consumer-lending-summary'
        disabled={!ready || clicked || notification?.severe}
      >
        {t('consumerLending:til.cta')}
      </Button>
    )
  }

  return (
    <>
      <Head title={`${t('summary:reviewOrder')} | ${t('summary:brandName')}`} />
      <div className={styles.header}>
        <Heading size={headingSize}>{t('consumerLending:summary.title')}</Heading>
      </div>
      <div className={isDesktop ? styles.bodyWrapperDesktop : styles.bodyWrapperMobile}>
        {status >= CheckoutStatus.Starting && <PlanOverview />}
        <StartedStatusGuard>
          {isExpress && (
            <>
              <div className={styles.shippingSummary}>
                {!isDeferredShipping && (
                  <div className={styles.shippingSummaryHeading}>
                    <Text size={sectionHeadingSize} renderAs='div' bold>
                      {isPickup ? t('common:orderCollectionDetails') : t('common:chooseShippingOptions')}
                    </Text>
                  </div>
                )}
                <ShippingSummary hasBorder={false} />
              </div>
            </>
          )}
          <OrderSummary />
          <PaymentMethod
            layout={PageLayout.Summary}
            negativeMargins={false}
            showFloatingHeader={false}
            changeLabel={t('paymentMethod:changeLabel')}
            warningBorder={notification?.severe}
            showErrorMessage={false}
          />
          {notification !== undefined && (
            <div className={styles.notification}>
              {notification.severe ? (
                <Text color='Fire' size={textSize}>
                  {notification?.message}
                </Text>
              ) : (
                <Message kind='Basic' iconName='Info' heading={notification.heading} description={notification?.message} />
              )}
            </div>
          )}
          {error !== undefined && <TrackOnMount eventName={TrackingEvent.VIEWED_NON_TERMINAL_ERROR} eventProperties={{ errorCode: error?.error }} />}
          {renderAutopayToggle()}
          <ConsumerLendingPlanDetails />
          <Checkbox
            id='tila-terms-checkbox'
            error={showTILACheckboxErrorMessage}
            label={<TILATerms setDocumentIdToShow={setDocumentIdToShow} />}
            onChange={handleTILATermsCheckboxChange}
          />
          {renderTILACheckboxErrorMessage()}
          {renderCTAButton()}
          <AutopayModal modalKey={AutopayConfirmationModalKey} autoPayAgreed={false} />
          <TruthInLendingModal documentId={documentId} isTilDisclosure={tilDisclosure} />
          {clicked && <DisabledModal />}
        </StartedStatusGuard>
      </div>
      <TrackOnMount eventName={TrackingEvent.VIEWED_SUMMARY_PAGE} eventProperties={{ summaryPageVersion }} />
    </>
  )
}
